import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Prompt, useHistory, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import { useTheme } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import AgentsAppBar, { AGENTS_APP_BAR_OFFSET_PX } from "../components/Agents/AgentsAppBar";
import AgentStatusControls, { statusColor } from "../components/Agents/AgentStatusControls";
import AgentGoalSection from "../components/Agents/AgentGoalSection";
import AgentWatchesSection from "../components/Agents/AgentWatchesSection";
import AgentActionsSection from "../components/Agents/AgentActionsSection";
import PreviewAgentModal from "../components/Agents/PreviewAgentModal";
import AgentDraftingAssistant, { COPILOT_FIXED_DOCK_PANEL_HEIGHT_CSS } from "../components/Agents/AgentDraftingAssistant";
import AgentKnowledgeSourcesSection from "../components/Agents/AgentKnowledgeSourcesSection";
import { describeApiFailure } from "../features/agents/executeFieldNormalize";
import { applyDraftToAgent, dedupeAgentTriggers } from "../features/agents/draftMapper";
import { syncAgentRulesWithActions } from "../features/agents/syncAgentRules";
import { ensureSkillsConsumerActionsOnAgent } from "../constants/builtInActionApps";
import {
  createAgent,
  enrichAgentWithCredentials,
  executeAgentRun,
  enrichToolOutputForBrain,
  prepareApiOutputForAgentBrain,
  assignStepUploadRefs,
  getAgent,
  pauseAgent,
  previewAgent,
  previewAgentNextStep,
  resumeBrowserRpa,
  extractBrowserRpaTask,
  findReusableBrowserSession,
  startAgent,
  stopAgent,
  syncProposedActionsWithExecuteFields,
  updateAgent,
  agentNeedsFormSampleCapture,
  getAgentFormPreviewUrl,
  ensureAgentFormTriggerRegistered,
  fetchAgentFormSubmissions,
  activateAgentWorkflowForListening,
  deactivateAgentWorkflowAfterListening,
  persistAgentFormSample,
} from "../features/agents/agentApi";
import { validateAgentConfig, agentNeedsPreviewBeforeStart } from "../features/agents/validateAgentConfig";
import { createDefaultAgent, createDefaultRule } from "../features/agents/types";
import { NavigationBlockerContext } from "../contexts/NavigationBlockerContext";

const parseJsonSafe = (value) => {
  if (value && typeof value === "object") return value;
  if (!value || String(value).trim() === "") return {};
  try {
    return JSON.parse(value);
  } catch (err) {
    return { raw: value };
  }
};

/** Human-readable action title for Preview timeline (never raw action_id alone). */
const humanReadableActionName = (action = {}, proposed = null) => {
  const app =
    proposed?.app_label ||
    proposed?.app ||
    action?.app_label ||
    action?.app ||
    action?.passportApp ||
    "";
  const label =
    proposed?.label ||
    action?.label ||
    action?.name ||
    action?.action_key ||
    action?.config?.action ||
    "";
  const appStr = String(app || "").trim();
  const labelStr = String(label || "").trim();
  if (appStr && labelStr) {
    const prefix = `${appStr}:`;
    if (labelStr.toLowerCase().startsWith(prefix.toLowerCase())) return labelStr;
    return `${appStr}: ${labelStr}`;
  }
  if (labelStr) return labelStr;
  if (appStr) return appStr;
  return "Action";
};

const summarizeActionsRun = (selection, runResultPayload, agentObj, proposedList, useIndices) => {
  const actionMap = {};
  (agentObj?.actions || []).forEach((a) => {
    if (a?.action_id) actionMap[a.action_id] = a;
  });
  const results = Array.isArray(runResultPayload?.results) ? runResultPayload.results : [];
  const ranActions = results.map((r, i) => {
    let proposed = null;
    if (useIndices && Array.isArray(proposedList)) {
      if (r?.proposal_index != null && proposedList[r.proposal_index]) {
        proposed = proposedList[r.proposal_index];
      } else if (typeof selection?.[i] === "number" && proposedList[selection[i]]) {
        proposed = proposedList[selection[i]];
      } else {
        proposed = proposedList.find((p) => p?.action_id === r?.action_id) || null;
      }
    } else if (Array.isArray(proposedList)) {
      proposed = proposedList.find((p) => p?.action_id === r?.action_id) || null;
    }
    const action = actionMap[r?.action_id] || {};
    let errorText = null;
    if (!r?.success) {
      const err = r?.error ?? r?.data?.error ?? r?.data;
      if (typeof err === "string") errorText = err;
      else if (err && typeof err === "object") {
        errorText = String(err.error || err.message || JSON.stringify(err)).slice(0, 400);
      } else errorText = "Failed";
    }
    const data = r?.data && typeof r.data === "object" ? r.data : {};
    const pageHint =
      data.preview_url ||
      data.preview_hint ||
      data.viewer_url ||
      data.published_page_url ||
      data.viewer_url_after_publish ||
      "";
    return {
      action_id: r?.action_id || "",
      label: humanReadableActionName(action, proposed),
      success: Boolean(r?.success),
      needsUserAction: Boolean(r?.needsUserAction || r?.data?.needsUserAction),
      reason: r?.reason || r?.data?.reason || r?.instructions?.reason || r?.data?.instructions?.reason || "",
      instructions: r?.instructions || r?.data?.instructions || null,
      error: errorText,
      pageHint: pageHint ? String(pageHint) : "",
      pageNote: data.publish_requires_user_approval
        ? "Draft only — open with ?preview=1 until Publish is approved."
        : data.preview_hint && !data.published_page_url
          ? "Draft created — use the preview link (not the public URL) until Publish."
          : "",
    };
  });
  return {
    type: "actions_run",
    approvedIds: selection,
    runResult: runResultPayload,
    ranActions,
  };
};

const mergeRunMemory = (existing = [], updates = [], maxItems = 18) => {
  const normalize = (item) => {
    if (typeof item === "string" && item.trim()) return { lesson: item.trim().slice(0, 500) };
    if (!item || typeof item !== "object") return null;
    const lesson = String(item.lesson || item.text || item.memory || "").trim();
    if (!lesson) return null;
    const row = { lesson: lesson.slice(0, 500) };
    if (item.action_id) row.action_id = String(item.action_id).slice(0, 200);
    if (item.action_key) row.action_key = String(item.action_key).slice(0, 200);
    if (item.label) row.label = String(item.label).slice(0, 200);
    if (Array.isArray(item.fields_hint) && item.fields_hint.length) row.fields_hint = item.fields_hint.slice(0, 8);
    if (item.source_run_id) row.source_run_id = item.source_run_id;
    return row;
  };
  const keyOf = (row) => {
    const aid = String(row.action_id || row.action_key || "").toLowerCase();
    const names = (row.fields_hint || [])
      .map((h) => String(h?.name || "").trim().toLowerCase())
      .filter(Boolean)
      .sort();
    if (names.length) return `${aid}|f:${names.join(",")}`;
    return `${aid}|general`;
  };
  const out = [];
  const seen = new Set();
  for (const raw of [...updates, ...existing]) {
    const row = normalize(raw);
    if (!row) continue;
    const k = keyOf(row);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(row);
    if (out.length >= maxItems) break;
  }
  return out;
};

const sanitizeBeforeSave = (agent) => ({
  ...agent,
  triggers: dedupeAgentTriggers(agent.triggers || []),
  run_memory: mergeRunMemory(agent.run_memory || [], [], 18),
  actions: (agent.actions || []).map((action) => ({
    ...action,
    config: parseJsonSafe(action.config),
  })),
});

/**
 * Fingerprint for "did instructions materially change?" — ignores formatting-only edits
 * (extra spaces, newlines, tabs, bullet/number markers) so Copilot does not re-run on
 * focus/blur or whitespace cleanup.
 */
const normalizeInstructionsForCompare = (text) =>
  String(text || "")
    .normalize("NFC")
    .replace(/\u00a0/g, " ")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) =>
      line
        .replace(/^\s*([-*•●◦]|\d+[.)])\s+/u, "")
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

const getAgentSnapshot = (agent) => JSON.stringify(sanitizeBeforeSave(agent));

const AgentDetailPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const location = useLocation();
  const userId = localStorage.getItem("user");
  const tenantId = localStorage.getItem("tenant");
  const { agentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startPhaseMessage, setStartPhaseMessage] = useState("");
  const [instructionsSaveStatus, setInstructionsSaveStatus] = useState(null);
  const [agent, setAgent] = useState(createDefaultAgent());
  const [banner, setBanner] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRunId, setPreviewRunId] = useState(null);
  const [previewTriggerEvent, setPreviewTriggerEvent] = useState(null);
  const [previewSteps, setPreviewSteps] = useState([]);
  const [previewPriorSteps, setPreviewPriorSteps] = useState([]);
  const [proposedActions, setProposedActions] = useState([]);
  const [previewReasoning, setPreviewReasoning] = useState("");
  const previewBedrockCallsRef = useRef([]);
  const [previewFromBrain, setPreviewFromBrain] = useState(false);
  /** Bumps when proposedActions are replaced so preview modal clears approve/skip state (avoids unstable JSON key resets). */
  const [previewApprovalEpoch, setPreviewApprovalEpoch] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPhase, setPreviewPhase] = useState(null);
  const [previewRunResult, setPreviewRunResult] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewUserActionPause, setPreviewUserActionPause] = useState(null);
  const [previewContinuingUserAction, setPreviewContinuingUserAction] = useState(false);
  /** Workload Forms: interactive sample capture before brain preview. */
  const [previewFormSample, setPreviewFormSample] = useState(null);
  const formListenIntervalRef = useRef(null);
  const formListenTimeoutRef = useRef(null);
  const formListenStartedAtRef = useRef(null);
  const formListenActivatedRef = useRef(false);
  const [copilotOpen, setCopilotOpen] = useState(() =>
    Boolean(String(location?.state?.initialPrompt || "").trim()),
  );
  /** Keep desktop dock mounted after first open so hide/show does not remount (and re-seed) Copilot. */
  const [copilotDockMounted, setCopilotDockMounted] = useState(() =>
    Boolean(String(location?.state?.initialPrompt || "").trim()),
  );
  const [copilotWsStatus, setCopilotWsStatus] = useState("connecting");
  const [copilotBusy, setCopilotBusy] = useState(false);
  /** One-shot seed from "Start building"; cleared after consume so resize remounts cannot re-submit. */
  const [copilotSeedPrompt, setCopilotSeedPrompt] = useState(
    () => String(location?.state?.initialPrompt || "").trim(),
  );
  const onCopilotConnectionStatus = useCallback((status) => {
    setCopilotWsStatus(status);
  }, []);
  const onCopilotBusyChange = useCallback((busy) => {
    setCopilotBusy(Boolean(busy));
  }, []);
  const onCopilotSeedConsumed = useCallback(() => {
    setCopilotSeedPrompt("");
  }, []);

  useEffect(() => {
    if (copilotOpen) setCopilotDockMounted(true);
  }, [copilotOpen]);

  const copilotConnectionBadge = useMemo(() => {
    const ok = { backgroundColor: "#2e7d32", color: "#fff", fontWeight: 600 };
    const bad = { backgroundColor: "#c62828", color: "#fff", fontWeight: 600 };
    const pending = {
      backgroundColor:
        theme.palette.type === "dark" ? theme.palette.action.hover : "rgba(0,0,0,0.08)",
      color: theme.palette.text.secondary,
      fontWeight: 600,
    };
    if (copilotWsStatus === "connected") {
      return <Chip size="small" label="Connected" style={ok} />;
    }
    if (copilotWsStatus === "connecting") {
      return <Chip size="small" label="Connecting…" style={pending} />;
    }
    return <Chip size="small" label="Connection issue" style={bad} />;
  }, [copilotWsStatus, theme.palette.type, theme.palette.action.hover, theme.palette.text.secondary]);
  const hadInstructionsRef = useRef(false);
  const lastSavedRef = useRef(null);
  const [lastSavedAt, setLastSavedAt] = useState(0);
  const agentRef = useRef(agent);
  agentRef.current = agent;
  const instructionsSaveTimerRef = useRef(null);
  const instructionsSaveInFlightRef = useRef(false);
  const instructionsSaveQueuedRef = useRef(false);
  const instructionsRefineTimerRef = useRef(null);
  const lastRefinedGoalRef = useRef(null);
  const copilotRef = useRef(null);
  const instructionsRefineQuietRef = useRef(false);

  const validation = useMemo(() => validateAgentConfig(agent), [agent]);
  const needsPreviewBeforeStart = useMemo(() => agentNeedsPreviewBeforeStart(agent), [agent]);
  const startDisabledReason = needsPreviewBeforeStart
    ? "Preview the agent at least once before starting it."
    : "";
  const isDirty = useMemo(
    () => agentId === "new" || lastSavedRef.current === null
      ? false
      : getAgentSnapshot(agent) !== lastSavedRef.current,
    [agent, lastSavedAt]
  );

  const { setBlocker } = useContext(NavigationBlockerContext) || {};
  const onSaveRef = useRef(null);
  useEffect(() => {
    if (!setBlocker) return;
    if (agentId === "new") {
      setBlocker(null);
      return;
    }
    setBlocker({
      isDirty: () => isDirty,
      onSave: () => onSaveRef.current(),
    });
    return () => setBlocker(null);
  }, [setBlocker, agentId, isDirty]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const loadData = async (id) => {
    setLoading(true);
    try {
      const existing = await getAgent(id);
      if (!existing) {
        setBanner({ severity: "error", message: "Agent not found." });
        return;
      }
      const healed = {
        ...existing,
        triggers: dedupeAgentTriggers(existing.triggers || []),
      };
      setAgent(healed);
      agentRef.current = healed;
      lastSavedRef.current = getAgentSnapshot(healed);
      setLastSavedAt(Date.now());
      lastRefinedGoalRef.current = normalizeInstructionsForCompare(healed.goal);
      // Persist collapse if API still has duplicate triggers (UI has no delete control).
      if ((existing.triggers || []).length > (healed.triggers || []).length) {
        try {
          const updated = await updateAgent(id, sanitizeBeforeSave(healed));
          if (updated) {
            setAgent(updated);
            lastSavedRef.current = getAgentSnapshot(updated);
            setLastSavedAt(Date.now());
            setBanner({
              severity: "info",
              message: "Removed duplicate trigger(s) — agents support one trigger.",
            });
          }
        } catch (_) {
          /* keep healed local state; user can Save */
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const boot = async () => {
      if (agentId === "new") {
        const created = await createAgent({ name: "Untitled Agent", status: "draft" });
        history.replace(`/agents/${created.agent_id}`);
        return;
      }
      await loadData(agentId);
    };
    boot();
  }, [agentId, history]);

  useEffect(() => {
    const hasInstructions = Boolean(agent?.goal && String(agent.goal).trim().length > 0);
    if (hasInstructions && !hadInstructionsRef.current) {
      setCopilotOpen(true);
    }
    hadInstructionsRef.current = hasInstructions;
  }, [agent?.goal]);

  useEffect(() => {
    const fromNav = String(location?.state?.initialPrompt || "").trim();
    if (!fromNav || agentId === "new") return;
    setCopilotSeedPrompt(fromNav);
    setCopilotOpen(true);
    // Drop navigation state so layout remounts (resize / mobile↔desktop) cannot re-seed.
    const nextState = { ...(location.state || {}) };
    delete nextState.initialPrompt;
    history.replace({ pathname: location.pathname, search: location.search, hash: location.hash, state: nextState });
    // Intentionally once per navigation seed — not on every location identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const onSave = async (nextAgent = null, options = null) => {
    const quiet = Boolean(options?.quiet);
    const target = nextAgent || agentRef.current || agent;
    if (instructionsSaveTimerRef.current) {
      clearTimeout(instructionsSaveTimerRef.current);
      instructionsSaveTimerRef.current = null;
    }
    setSaving(true);
    if (quiet) setInstructionsSaveStatus("Saving…");
    try {
      const sanitized = sanitizeBeforeSave(target);
      const updated = await updateAgent(agentId, sanitized);
      setAgent(updated);
      agentRef.current = updated;
      lastSavedRef.current = getAgentSnapshot(updated);
      setLastSavedAt(Date.now());
      if (quiet) {
        setInstructionsSaveStatus("Saved");
        window.setTimeout(() => {
          setInstructionsSaveStatus((prev) => (prev === "Saved" ? null : prev));
        }, 1600);
      } else {
        setBanner({ severity: "success", message: "Agent saved." });
        setInstructionsSaveStatus(null);
      }
      return true;
    } catch (err) {
      if (quiet) {
        setInstructionsSaveStatus("Save failed");
        setBanner({ severity: "error", message: err?.message || "Failed to save instructions." });
      } else {
        setBanner({ severity: "error", message: "Failed to save agent." });
      }
      return false;
    } finally {
      setSaving(false);
    }
  };
  onSaveRef.current = onSave;

  const flushInstructionsSave = useCallback(async () => {
    if (agentId === "new" || !agentId) return false;
    if (instructionsSaveTimerRef.current) {
      clearTimeout(instructionsSaveTimerRef.current);
      instructionsSaveTimerRef.current = null;
    }
    const latest = agentRef.current;
    if (!latest) return false;
    if (lastSavedRef.current != null && getAgentSnapshot(latest) === lastSavedRef.current) {
      return true;
    }
    if (instructionsSaveInFlightRef.current) {
      instructionsSaveQueuedRef.current = true;
      return false;
    }
    instructionsSaveInFlightRef.current = true;
    try {
      const ok = await onSaveRef.current?.(latest, { quiet: true });
      return Boolean(ok);
    } finally {
      instructionsSaveInFlightRef.current = false;
      if (instructionsSaveQueuedRef.current) {
        instructionsSaveQueuedRef.current = false;
        void flushInstructionsSave();
      }
    }
  }, [agentId]);

  const refineConfigFromInstructions = useCallback(async () => {
    if (agentId === "new" || !agentId) return;
    if (instructionsRefineTimerRef.current) {
      clearTimeout(instructionsRefineTimerRef.current);
      instructionsRefineTimerRef.current = null;
    }
    await flushInstructionsSave();
    const goal = String(agentRef.current?.goal || "").trim();
    const fingerprint = normalizeInstructionsForCompare(goal);
    // No text, click-in/out only, or formatting-only change (spaces/newlines/bullets).
    if (!fingerprint || fingerprint === lastRefinedGoalRef.current) return;
    lastRefinedGoalRef.current = fingerprint;
    setCopilotOpen(true);
    setInstructionsSaveStatus("Updating config from instructions…");
    instructionsRefineQuietRef.current = true;
    let attempts = 0;
    const tryRefine = () => {
      attempts += 1;
      const started = copilotRef.current?.refineFromInstructions?.(goal);
      if (started) return;
      if (attempts < 24) {
        window.setTimeout(tryRefine, 250);
        return;
      }
      instructionsRefineQuietRef.current = false;
      setInstructionsSaveStatus("Open AI Copilot to update config");
      setBanner({
        severity: "info",
        message:
          "Instructions saved. Could not reach AI Copilot to refresh watches/actions/rules — open Copilot and try editing instructions again.",
      });
    };
    window.setTimeout(tryRefine, 150);
  }, [agentId, flushInstructionsSave]);

  useEffect(() => {
    return () => {
      if (instructionsSaveTimerRef.current) clearTimeout(instructionsSaveTimerRef.current);
      if (instructionsRefineTimerRef.current) clearTimeout(instructionsRefineTimerRef.current);
    };
  }, []);

  // Local-only while typing — never autosave. Persist only via Save (instructions) or name blur.
  const onInstructionsChange = useCallback((patch) => {
    setAgent((prev) => {
      const next = { ...prev, ...patch };
      agentRef.current = next;
      return next;
    });
  }, []);

  const handleBack = async () => {
    if (isDirty) {
      const saved = await onSave();
      if (!saved) return;
    }
    history.push("/agents");
  };

  const friendlyStartError = (err, phase = "") => {
    const raw = String(err?.response?.data?.error || err?.response?.data?.message || err?.message || "").trim();
    const lower = raw.toLowerCase();
    if (phase === "save" || lower.includes("save")) {
      return raw
        ? `Couldn't save the agent before starting: ${raw}`
        : "Couldn't save the agent before starting. Check your connection and try again.";
    }
    if (lower.includes("trigger") || lower.includes("createtrigger") || phase === "trigger") {
      return raw
        ? `Agent was activated, but creating the trigger failed: ${raw}`
        : "Agent was activated, but creating the trigger failed. Try Start again, or check the trigger credentials and configuration.";
    }
    if (lower.includes("timeout") || lower.includes("network") || err?.code === "ECONNABORTED") {
      return "Starting timed out. Check your connection and try again.";
    }
    return raw || "Failed to start the agent. Please try again.";
  };

  const onStart = async () => {
    if (starting) return;
    if (!validation.canStart) {
      setBanner({ severity: "warning", message: "Fix validation issues before starting." });
      return;
    }
    if (agentNeedsPreviewBeforeStart(agent)) {
      setBanner({
        severity: "warning",
        message: "Preview the agent at least once before starting it.",
      });
      return;
    }
    setStarting(true);
    setStartPhaseMessage("Saving agent…");
    setBanner(null);
    try {
      const saved = await onSave(null, { quiet: true });
      if (!saved) {
        setBanner((prev) => ({
          severity: "error",
          message:
            prev?.severity === "error" && prev?.message
              ? `Couldn't save the agent before starting: ${prev.message}`
              : friendlyStartError(null, "save"),
        }));
        return;
      }
      setStartPhaseMessage("Activating agent…");
      const updated = await startAgent(agentId, {
        onPhase: (_id, message) => {
          if (message) setStartPhaseMessage(message);
        },
      });
      setAgent(updated);
      if (updated) {
        lastSavedRef.current = getAgentSnapshot(updated);
        setLastSavedAt(Date.now());
      }
      setBanner({ severity: "success", message: "Agent is running." });
    } catch (err) {
      setBanner({
        severity: "error",
        message: friendlyStartError(err),
      });
    } finally {
      setStarting(false);
      setStartPhaseMessage("");
    }
  };

  const onPause = async () => {
    const updated = await pauseAgent(agentId);
    setAgent(updated);
    if (updated) {
      lastSavedRef.current = getAgentSnapshot(updated);
      setLastSavedAt(Date.now());
    }
  };

  const onStop = async () => {
    const updated = await stopAgent(agentId);
    setAgent(updated);
    if (updated) {
      lastSavedRef.current = getAgentSnapshot(updated);
      setLastSavedAt(Date.now());
    }
  };

  const FORM_LISTEN_POLL_MS = 2000;
  const FORM_LISTEN_TIMEOUT_MS = 5 * 60 * 1000;

  const stopFormListenPolling = useCallback(() => {
    if (formListenIntervalRef.current) {
      clearInterval(formListenIntervalRef.current);
      formListenIntervalRef.current = null;
    }
    if (formListenTimeoutRef.current) {
      clearTimeout(formListenTimeoutRef.current);
      formListenTimeoutRef.current = null;
    }
  }, []);

  const teardownFormListening = useCallback(
    async (currentAgent) => {
      stopFormListenPolling();
      formListenStartedAtRef.current = null;
      if (formListenActivatedRef.current) {
        formListenActivatedRef.current = false;
        const ag = currentAgent || agent;
        try {
          await deactivateAgentWorkflowAfterListening(ag);
        } catch (_) {
          /* best-effort */
        }
      }
    },
    [agent, stopFormListenPolling],
  );

  const continuePreviewWithSample = useCallback(
    async (samplePayload, agentSnapshot = null) => {
      const ag = agentSnapshot || agent;
      setPreviewFormSample(null);
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewPhase({ id: "fetch_sample", message: "Using form submission…" });
      try {
        const trigger = (ag?.triggers || [])[0];
        const triggerEvent = {
          source: trigger?.source || "workload-forms",
          type: trigger?.type || trigger?.trigger_key || "Form submission",
          payload: samplePayload,
        };
        const result = await previewAgent(agentId, triggerEvent, {
          onPhase: (id, message) => setPreviewPhase({ id, message }),
        });
        if (!result) {
          setPreviewError("Preview unavailable (check WebSocket / agent connection).");
          return;
        }
        setPreviewRunId(result.run_id || null);
        setPreviewTriggerEvent(result.event || triggerEvent);
        setPreviewFromBrain(Boolean(result.from_brain));
        if (result.error) setPreviewError(result.error);
        setPreviewPriorSteps([]);
        setProposedActions(
          syncProposedActionsWithExecuteFields(ag, result.proposed_actions || [], {
            priorSteps: [],
          }),
        );
        setPreviewReasoning(result.reasoning || "");
        if (Array.isArray(result.bedrock_calls) && result.bedrock_calls.length) {
          previewBedrockCallsRef.current = previewBedrockCallsRef.current.concat(result.bedrock_calls);
        }
        setPreviewSteps([]);
        setPreviewApprovalEpoch((e) => e + 1);
        const previewedAt = new Date().toISOString();
        setAgent((prev) => (prev ? { ...prev, previewed_at: previewedAt } : prev));
        updateAgent(agentId, { previewed_at: previewedAt }).catch((err) => {
          console.warn("Failed to persist previewed_at:", err?.message || err);
        });
      } catch (err) {
        setPreviewError(err?.message || "Preview failed.");
        setBanner({ severity: "error", message: err?.message || "Preview failed." });
      } finally {
        setPreviewLoading(false);
        setPreviewPhase(null);
      }
    },
    [agent, agentId],
  );

  const onFormSampleReady = useCallback(
    async (samplePayload) => {
      if (!samplePayload || typeof samplePayload !== "object") return;
      stopFormListenPolling();
      const ag = agent;
      try {
        await teardownFormListening(ag);
        setPreviewPhase({ id: "fetch_sample", message: "Saving form sample…" });
        setPreviewLoading(true);
        const updated = await persistAgentFormSample(agentId, ag, samplePayload);
        if (updated) {
          setAgent(updated);
          lastSavedRef.current = getAgentSnapshot(updated);
        }
        await continuePreviewWithSample(samplePayload, updated || ag);
      } catch (err) {
        setPreviewLoading(false);
        setPreviewPhase({ id: "wait_form", message: "Waiting for form submission…" });
        setPreviewFormSample((prev) =>
          prev
            ? { ...prev, listening: false, error: err?.message || "Failed to save form sample." }
            : null,
        );
      }
    },
    [agent, agentId, continuePreviewWithSample, stopFormListenPolling, teardownFormListening],
  );

  const startFormListenPolling = useCallback(
    (workflowId, triggerId) => {
      stopFormListenPolling();
      const sinceTs = formListenStartedAtRef.current || Date.now();
      const pollOnce = async () => {
        try {
          const subs = await fetchAgentFormSubmissions(workflowId, {
            sinceTs,
            triggerId,
            agentId,
          });
          // sinceTs already softened inside fetch; take newest match.
          const fresh = subs[0];
          if (fresh?.payload) {
            await onFormSampleReady(fresh.payload);
          }
        } catch (_) {
          /* keep polling */
        }
      };
      formListenIntervalRef.current = setInterval(pollOnce, FORM_LISTEN_POLL_MS);
      formListenTimeoutRef.current = setTimeout(() => {
        stopFormListenPolling();
        setPreviewFormSample((prev) =>
          prev
            ? {
                ...prev,
                listening: false,
                timedOut: true,
                error: "Timed out waiting for a submission. Open the form and submit again, or retry.",
              }
            : null,
        );
        setPreviewPhase({ id: "wait_form", message: "Waiting for form submission…" });
      }, FORM_LISTEN_TIMEOUT_MS);
      // Immediate check in case a submission already landed
      pollOnce();
    },
    [FORM_LISTEN_POLL_MS, FORM_LISTEN_TIMEOUT_MS, agentId, onFormSampleReady, stopFormListenPolling],
  );

  const onStartFormListen = useCallback(async () => {
    const workflowId = String(agent?.workflow_id || previewFormSample?.workflowId || "").trim();
    if (!workflowId) {
      setPreviewFormSample((prev) =>
        prev ? { ...prev, error: "Missing workflow id — save the agent and try again." } : null,
      );
      return;
    }
    try {
      setPreviewFormSample((prev) =>
        prev
          ? { ...prev, listening: true, preparing: false, timedOut: false, error: null }
          : prev,
      );
      setPreviewPhase({ id: "wait_form", message: "Listening for a form submission…" });
      if (!formListenActivatedRef.current) {
        await activateAgentWorkflowForListening(agent);
        formListenActivatedRef.current = true;
      }
      // Keep original listen window if already listening (Open form again).
      if (!formListenStartedAtRef.current) {
        formListenStartedAtRef.current = Date.now();
      }
      const triggerId = (agent?.triggers || [])[0]?.trigger_id || "";
      startFormListenPolling(workflowId, triggerId);
    } catch (err) {
      setPreviewFormSample((prev) =>
        prev
          ? {
              ...prev,
              listening: false,
              error: err?.message || "Could not start listening for form submissions.",
            }
          : null,
      );
    }
  }, [agent, previewFormSample?.workflowId, startFormListenPolling]);

  const onUseLatestFormSample = useCallback(
    async (payload) => {
      if (payload) await onFormSampleReady(payload);
    },
    [onFormSampleReady],
  );

  const onCancelFormSampleWait = useCallback(async () => {
    await teardownFormListening(agent);
    setPreviewFormSample(null);
    setPreviewPhase(null);
    setPreviewLoading(false);
  }, [agent, teardownFormListening]);

  const onRetryFormListen = useCallback(() => {
    formListenStartedAtRef.current = null;
    onStartFormListen();
  }, [onStartFormListen]);

  useEffect(
    () => () => {
      stopFormListenPolling();
      if (formListenActivatedRef.current) {
        formListenActivatedRef.current = false;
        deactivateAgentWorkflowAfterListening(agentRef.current).catch(() => {});
      }
    },
    [stopFormListenPolling],
  );

  const ensureSkillsToolsForPreview = useCallback(
    async (ag) => {
      try {
        const uid =
          (typeof localStorage !== "undefined" && localStorage.getItem("user")) ||
          ag?.created_by_user_id ||
          "";
        const next = ensureSkillsConsumerActionsOnAgent(ag, { userId: uid });
        if (!next || next === ag) return ag;
        const before = JSON.stringify(ag?.actions || []);
        const after = JSON.stringify(next.actions || []);
        const policyChanged =
          Boolean(next?.policy?.skills_consumer_enabled) !==
          Boolean(ag?.policy?.skills_consumer_enabled);
        if (before === after && !policyChanged) return ag;
        setAgent(next);
        agentRef.current = next;
        await updateAgent(agentId, {
          actions: next.actions,
          policy: next.policy,
        });
        return next;
      } catch (err) {
        console.warn("ensureSkillsToolsForPreview failed:", err?.message || err);
        return ag;
      }
    },
    [agentId],
  );

  const runBrainPreview = async (agentSnapshot) => {
    let ag = agentSnapshot || agent;
    ag = (await ensureSkillsToolsForPreview(ag)) || ag;
    setPreviewLoading(true);
    try {
      const result = await previewAgent(agentId, null, {
        onPhase: (id, message) => setPreviewPhase({ id, message }),
      });
      if (!result) {
        setPreviewError("Preview unavailable (check WebSocket / agent connection).");
        return;
      }
      setPreviewRunId(result.run_id || null);
      setPreviewTriggerEvent(result.event || null);
      setPreviewFromBrain(Boolean(result.from_brain));
      if (result.error) setPreviewError(result.error);
      setPreviewPriorSteps([]);
      setProposedActions(
        syncProposedActionsWithExecuteFields(ag, result.proposed_actions || [], {
          priorSteps: [],
        }),
      );
      setPreviewReasoning(result.reasoning || "");
      if (Array.isArray(result.bedrock_calls) && result.bedrock_calls.length) {
        previewBedrockCallsRef.current = previewBedrockCallsRef.current.concat(result.bedrock_calls);
      }
      setPreviewSteps([]);
      setPreviewApprovalEpoch((e) => e + 1);
      const previewedAt = new Date().toISOString();
      setAgent((prev) => (prev ? { ...prev, previewed_at: previewedAt } : prev));
      updateAgent(agentId, { previewed_at: previewedAt }).catch((err) => {
        console.warn("Failed to persist previewed_at:", err?.message || err);
      });
    } catch (err) {
      setPreviewError(err?.message || "Preview failed.");
      setBanner({ severity: "error", message: err?.message || "Preview failed." });
    } finally {
      setPreviewLoading(false);
      setPreviewPhase(null);
    }
  };

  const onPreviewAgent = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewRunResult(null);
    setPreviewError(null);
    setPreviewUserActionPause(null);
    setPreviewFormSample(null);
    setPreviewRunId(null);
    setPreviewTriggerEvent(null);
    setPreviewFromBrain(false);
    setPreviewPriorSteps([]);
    setProposedActions([]);
    setPreviewReasoning("");
    previewBedrockCallsRef.current = [];
    setPreviewSteps([]);
    setPreviewPhase({ id: "save", message: "Saving agent…" });
    stopFormListenPolling();
    formListenActivatedRef.current = false;
    const saved = await onSave();
    if (!saved) {
      setPreviewLoading(false);
      setPreviewPhase(null);
      setPreviewOpen(false);
      return;
    }
    try {
      // Re-load after save so workflow_id / trigger fields are current
      let current = (await getAgent(agentId)) || agent;
      // Persist skills/pages tools before brain preview so Approve can execute them
      // (planning injects them even when Tools was empty).
      current = (await ensureSkillsToolsForPreview(current)) || current;
      if (agentNeedsFormSampleCapture(current)) {
        setPreviewPhase({ id: "wait_form", message: "Preparing form…" });
        setPreviewFormSample({
          formUrl: getAgentFormPreviewUrl(current),
          workflowId: String(current.workflow_id || ""),
          listening: false,
          preparing: true,
          error: null,
          latestSubmission: null,
          timedOut: false,
        });
        setPreviewLoading(false);
        try {
          const registered = await ensureAgentFormTriggerRegistered(current);
          if (registered) {
            current = registered;
            setAgent(registered);
          }
          const formUrl = getAgentFormPreviewUrl(current);
          if (!formUrl) {
            setPreviewFormSample((prev) =>
              prev
                ? {
                    ...prev,
                    preparing: false,
                    error: "Form URL unavailable — save the agent so a workflow id is created.",
                  }
                : null,
            );
            setPreviewPhase({ id: "wait_form", message: "Waiting for form submission…" });
            return;
          }
          const triggerId = (current.triggers || [])[0]?.trigger_id || "";
          let latestSubmission = null;
          try {
            const subs = await fetchAgentFormSubmissions(current.workflow_id, {
              triggerId,
              agentId,
            });
            if (subs[0]) latestSubmission = subs[0];
          } catch (_) {
            /* optional */
          }
          setPreviewFormSample({
            formUrl,
            workflowId: String(current.workflow_id || ""),
            listening: false,
            preparing: false,
            error: null,
            latestSubmission,
            timedOut: false,
          });
          setPreviewPhase({ id: "wait_form", message: "Waiting for form submission…" });
        } catch (err) {
          setPreviewFormSample((prev) =>
            prev
              ? {
                  ...prev,
                  preparing: false,
                  formUrl: getAgentFormPreviewUrl(current),
                  error: err?.message || "Could not register the form trigger.",
                }
              : {
                  formUrl: getAgentFormPreviewUrl(current),
                  workflowId: String(current.workflow_id || ""),
                  listening: false,
                  preparing: false,
                  error: err?.message || "Could not register the form trigger.",
                  latestSubmission: null,
                  timedOut: false,
                },
          );
          setPreviewPhase({ id: "wait_form", message: "Waiting for form submission…" });
        }
        return;
      }
      await runBrainPreview(current);
    } catch (err) {
      setPreviewError(err?.message || "Preview failed.");
      setBanner({ severity: "error", message: err?.message || "Preview failed." });
      setPreviewLoading(false);
      setPreviewPhase(null);
    }
  };

  const onRunApproved = async (runId, selection) => {
    setPreviewLoading(true);
    setPreviewRunResult(null);
    setPreviewError(null);
    setPreviewPhase({ id: "run_actions", message: "Running approved actions…" });
    try {
      const proposedForRun = previewFromBrain ? proposedActions : null;
      const reuseSession = findReusableBrowserSession(previewPriorSteps);
      const bedrockCalls = previewBedrockCallsRef.current.slice();
      previewBedrockCallsRef.current = [];
      const run = await executeAgentRun(
        agentId,
        runId,
        selection,
        agent,
        proposedForRun,
        previewFromBrain
          ? {
              useProposalIndices: true,
              agent_reasoning: previewReasoning || "",
              bedrock_calls: bedrockCalls,
              browserSessionReuse: reuseSession,
              priorSteps: previewPriorSteps,
            }
          : {
              ...(reuseSession ? { browserSessionReuse: reuseSession } : {}),
              priorSteps: previewPriorSteps,
            },
      );
      const refreshed = await getAgent(agentId);
      if (refreshed) setAgent(refreshed);
      const runResultPayload = {
        success: run.status !== "error",
        results: run.results || [],
        error: run.error || (run.status === "error" ? "Run failed." : run.status === "halted" ? "Run halted — no progress." : run.status === "partial_success" ? "Run finished with partial success." : run.status === "skipped" ? "Run skipped by criteria." : null),
      };
      setPreviewRunResult(runResultPayload);

      // Phase D: pause for Live View when Browser RPA needs MFA/CAPTCHA
      const pauseHit = (run.results || []).find(
        (r) => r?.needsUserAction || r?.data?.needsUserAction,
      );
      if (pauseHit) {
        const data = pauseHit.data || pauseHit;
        const actionMapPause = {};
        (agent?.actions || []).forEach((a) => {
          if (a?.action_id) actionMapPause[a.action_id] = a;
        });
        const pauseAction = actionMapPause[pauseHit.action_id] || {};
        const proposedForPause =
          (Array.isArray(proposedActions) &&
            proposedActions.find(
              (p, idx) =>
                (pauseHit.proposal_index != null && idx === pauseHit.proposal_index) ||
                p?.action_id === pauseHit.action_id,
            )) ||
          null;
        const pendingTask = extractBrowserRpaTask(pauseAction, proposedForPause);
        setPreviewUserActionPause({
          needsUserAction: true,
          liveViewUrl: pauseHit.liveViewUrl || data.liveViewUrl || "",
          liveViewError: data.liveViewError || null,
          viewportWidth: data.viewportWidth || data.liveViewViewport?.width || 1440,
          viewportHeight: data.viewportHeight || data.liveViewViewport?.height || 900,
          liveViewViewport: data.liveViewViewport || null,
          instructions: pauseHit.instructions || data.instructions || null,
          agentDid: data.agentDid || [],
          reason: data.reason || pauseHit.instructions?.reason || data.instructions?.reason || "",
          sessionId: pauseHit.sessionId || data.sessionId || "",
          profileId:
            pauseHit.profileId ||
            data.profileId ||
            pauseAction.config?.profileId ||
            "",
          app:
            data.app ||
            pauseAction.app ||
            pauseAction.app_label ||
            pauseAction.passportApp ||
            "",
          loginRecipe:
            data.loginRecipe ||
            pauseAction.config?.loginRecipe ||
            pauseAction.loginRecipe ||
            null,
          pendingGoal: pendingTask.goal || "",
          pendingActions: pendingTask.actions || null,
          actionId: pauseHit.action_id,
        });
        setPreviewLoading(false);
        setPreviewPhase(null);
        const pauseReason =
          data.reason ||
          pauseHit.instructions?.reason ||
          data.instructions?.reason ||
          "";
        setBanner({
          severity: "info",
          message:
            pauseReason === "fill_failed"
              ? "Automation couldn't fill the login form — take over in Live View, then Continue so we can learn it."
              : pauseReason === "assist_timeout"
                ? "Login didn't finish in time — take over in Live View, then Continue so we can remember it."
                : pauseReason === "task_step_failed" || pauseReason === "task_failed"
                  ? "Automation stuck on the next step — finish it in Live View, then Continue so we can learn it."
                  : "Complete the step in Live View, then click Continue.",
        });
        return;
      }
      setPreviewUserActionPause(null);
      setBanner({ severity: "success", message: "Approved actions run." });

      const actionMap = {};
      (agent.actions || []).forEach((a) => { if (a?.action_id) actionMap[a.action_id] = a; });
      setPreviewPhase({ id: "enrich_outputs", message: "Reading action results…" });
      const newPriorSteps = await Promise.all(
        (run.results || []).map(async (r) => {
          let output = r.success ? r.data : r.error || r.data;
          const hint = describeApiFailure(output);
          if (r.success === false || hint) {
            const body = output && typeof output === "object" ? { ...output } : { message: output };
            output = {
              ...body,
              ok: false,
              error: body.error || hint || "Tool failed",
              ...(r.sent_request ? { sent_request: r.sent_request } : {}),
            };
          } else if (output != null) {
            output = await enrichToolOutputForBrain(output);
            // Agents use the real API body; drop workflow-editor list→dict duplicate.
            output = prepareApiOutputForAgentBrain(output);
          }
          return {
            action_id: r.action_id,
            label: (actionMap[r.action_id] || {}).label || r.action_id,
            output,
          };
        }),
      );
      const updatedPriorSteps = assignStepUploadRefs([...previewPriorSteps, ...newPriorSteps]);
      setPreviewPriorSteps(updatedPriorSteps);

      if (!previewTriggerEvent) {
        setPreviewLoading(false);
        setPreviewPhase(null);
        return;
      }
      const nextResult = await previewAgentNextStep(agent, previewTriggerEvent, {
        priorSteps: updatedPriorSteps,
        onPhase: (id, message) => setPreviewPhase({ id, message }),
      });
      setPreviewRunResult(null);
      setPreviewPriorSteps(updatedPriorSteps);
      if (!nextResult) {
        setPreviewError(
          "Could not load next step (preview timed out or connection closed). Long photo download/OCR batches need more time — try approving fewer actions per step, or retry.",
        );
        setPreviewReasoning("");
        setProposedActions([]);
        setPreviewApprovalEpoch((e) => e + 1);
        setPreviewSteps((prev) => [
          ...prev,
          summarizeActionsRun(
            selection,
            runResultPayload,
            agent,
            proposedForRun,
            previewFromBrain,
          ),
        ]);
        return;
      }
      if (nextResult.error) setPreviewError(nextResult.error);
      const agentAfterBind = (await persistWorkloadCodeBindings(nextResult, agent)) || agent;
      const nextActions = syncProposedActionsWithExecuteFields(
        agentAfterBind,
        nextResult.proposed_actions ?? [],
        {
          priorSteps: updatedPriorSteps,
          browserSessionReuse: findReusableBrowserSession(updatedPriorSteps),
        },
      );
      setPreviewReasoning(nextResult.reasoning ?? "");
      if (Array.isArray(nextResult.bedrock_calls) && nextResult.bedrock_calls.length) {
        previewBedrockCallsRef.current = previewBedrockCallsRef.current.concat(nextResult.bedrock_calls);
      }
      setProposedActions(nextActions);
      setPreviewApprovalEpoch((e) => e + 1);
      setPreviewSteps((prev) => {
        const out = [
          ...prev,
          summarizeActionsRun(
            selection,
            runResultPayload,
            agentAfterBind,
            proposedForRun,
            previewFromBrain,
          ),
        ];
        if (nextActions.length === 0 && !out.some((t) => t.type === "cycle_complete")) {
          out.push({ type: "cycle_complete", content: "Run cycle complete." });
        }
        return out;
      });
      await persistMemoryUpdates(nextResult.memory_updates);
    } catch (err) {
      setPreviewRunResult({ success: false, error: err?.message || "Run failed." });
      setBanner({ severity: "error", message: err?.message || "Run failed." });
    } finally {
      setPreviewLoading(false);
      setPreviewPhase(null);
    }
  };

  const persistWorkloadCodeBindings = async (previewResult, agentSnapshot = null) => {
    const bindings = previewResult?.workload_code_bindings;
    const patchedActions = previewResult?.actions;
    if (!Array.isArray(bindings) || bindings.length === 0) return;
    if (!Array.isArray(patchedActions) || patchedActions.length === 0) return;
    const ag = agentSnapshot || agent;
    try {
      await updateAgent(agentId, { actions: patchedActions });
      const updated = await getAgent(agentId);
      if (updated) setAgent(updated);
      else setAgent((prev) => ({ ...prev, actions: patchedActions }));
      const frozen = bindings.filter((b) => b && b.status === "frozen" && b.action_id).length;
      const pending = bindings.filter((b) => b && b.status === "pending" && b.action_id).length;
      if (frozen || pending) {
        setBanner({
          severity: pending && !frozen ? "info" : "success",
          message: pending
            ? `Workload Code bind: ${frozen} frozen, ${pending} still pending (need more sample shapes).`
            : `Workload Code bound and frozen (${frozen} step${frozen === 1 ? "" : "s"}).`,
        });
      }
      return updated || { ...ag, actions: patchedActions };
    } catch (err) {
      console.warn("Failed to persist workload_code_bindings:", err?.message || err);
      return null;
    }
  };

  const persistMemoryUpdates = async (memoryUpdates) => {
    if (!Array.isArray(memoryUpdates) || memoryUpdates.length === 0) return;
    try {
      const merged = mergeRunMemory(agent.run_memory || [], memoryUpdates);
      await updateAgent(agentId, { run_memory: merged });
      const updated = await getAgent(agentId);
      if (updated) setAgent(updated);
      else setAgent((prev) => ({ ...prev, run_memory: merged }));
    } catch (err) {
      console.warn("Failed to persist agent run_memory:", err?.message || err);
    }
  };

  const onSendReply = async (userMessage) => {
    if (!previewTriggerEvent || !userMessage?.trim()) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewPhase({ id: "brain", message: "Updating plan from your reply…" });
    setPreviewSteps((prev) => [...prev, { type: "user_message", content: userMessage.trim() }]);
    try {
      const nextResult = await previewAgentNextStep(agent, previewTriggerEvent, {
        priorSteps: previewPriorSteps,
        userMessage: userMessage.trim(),
        onPhase: (id, message) => setPreviewPhase({ id, message }),
      });
      if (!nextResult) {
        setPreviewError("Could not load reply step (preview connection closed).");
        setPreviewLoading(false);
        setPreviewPhase(null);
        return;
      }
      if (nextResult.error) setPreviewError(nextResult.error);
      setPreviewReasoning(nextResult.reasoning ?? "");
      if (Array.isArray(nextResult.bedrock_calls) && nextResult.bedrock_calls.length) {
        previewBedrockCallsRef.current = previewBedrockCallsRef.current.concat(nextResult.bedrock_calls);
      }
      const agentAfterBind = (await persistWorkloadCodeBindings(nextResult, agent)) || agent;
      setProposedActions(
        syncProposedActionsWithExecuteFields(agentAfterBind, nextResult.proposed_actions ?? [], {
          priorSteps: previewPriorSteps,
          browserSessionReuse: findReusableBrowserSession(previewPriorSteps),
        }),
      );
      setPreviewApprovalEpoch((e) => e + 1);

      const instructionUpdate = nextResult.instruction_update?.trim();
      const rulesUpdate = nextResult?.rules_update;
      const memoryUpdates = nextResult?.memory_updates;
      const validActionIds = new Set((agent.actions || []).map((a) => a.action_id).filter(Boolean));
      if (
        instructionUpdate ||
        (Array.isArray(rulesUpdate) && rulesUpdate.length > 0) ||
        (Array.isArray(memoryUpdates) && memoryUpdates.length > 0)
      ) {
        try {
          const patch = {};
          if (instructionUpdate) {
            const currentGoal = (agent.goal || "").trim();
            patch.goal = currentGoal ? `${currentGoal}\n\n${instructionUpdate}` : instructionUpdate;
          }
          if (Array.isArray(rulesUpdate) && rulesUpdate.length > 0) {
            const newRules = rulesUpdate.map((r) => {
              const base = createDefaultRule();
              const actionIds = (r.action_ids || []).filter((id) => validActionIds.has(id));
              return {
                ...base,
                name: r.name || base.name,
                when: r.when || base.when,
                if: r.if ?? base.if,
                action_ids: actionIds.length > 0 ? actionIds : (agent.actions || [])[0]?.action_id ? [(agent.actions || [])[0].action_id] : [],
              };
            });
            patch.rules = [...(agent.rules || []), ...newRules];
          }
          if (Array.isArray(memoryUpdates) && memoryUpdates.length > 0) {
            patch.run_memory = mergeRunMemory(agent.run_memory || [], memoryUpdates);
          }
          if (Object.keys(patch).length > 0) {
            await updateAgent(agentId, patch);
            const updated = await getAgent(agentId);
            if (updated) setAgent(updated);
            setBanner({ severity: "success", message: "Agent updated with your feedback." });
          }
        } catch (persistErr) {
          setBanner({ severity: "warning", message: persistErr?.message || "Feedback applied in preview; save failed." });
        }
      }
    } catch (err) {
      setPreviewError(err?.message || "Failed to get next step.");
      setBanner({ severity: "error", message: err?.message || "Failed to get next step." });
    } finally {
      setPreviewLoading(false);
      setPreviewPhase(null);
    }
  };

  const onClosePreview = () => {
    if (previewLoading && !previewFormSample) return;
    teardownFormListening(agent);
    setPreviewFormSample(null);
    setPreviewOpen(false);
    setPreviewRunResult(null);
    setPreviewSteps([]);
    setPreviewPriorSteps([]);
    setPreviewTriggerEvent(null);
    setPreviewError(null);
    setPreviewPhase(null);
    setPreviewUserActionPause(null);
    setPreviewContinuingUserAction(false);
  };

  const onContinueUserAction = async (pause) => {
    if (!pause?.sessionId) {
      setPreviewError("Missing browser session — cannot continue.");
      return;
    }
    setPreviewContinuingUserAction(true);
    setPreviewError(null);
    setPreviewPhase({ id: "run_actions", message: "Resuming after login…" });
    try {
      const actionMap = {};
      (agent?.actions || []).forEach((a) => {
        if (a?.action_id) actionMap[a.action_id] = a;
      });
      const action = actionMap[pause.actionId] || {};
      const appSlug =
        pause.app ||
        action.app ||
        action.app_label ||
        action.passportApp ||
        "";
      const recipe =
        pause.loginRecipe ||
        action.config?.loginRecipe ||
        action.loginRecipe ||
        null;
      const pendingFromAction = extractBrowserRpaTask(action);
      const pendingGoal = pause.pendingGoal || pendingFromAction.goal || "";
      const pendingActions = pause.pendingActions || pendingFromAction.actions || null;
      let tenantId = "";
      let userId = "";
      try {
        tenantId = localStorage.getItem("tenant") || "";
        userId = localStorage.getItem("user") || "";
      } catch (_) {
        /* ignore */
      }
      // Resume on the SAME session and run the original Browser Task goal — do not
      // start a new session at loginUrl.
      const resume = await resumeBrowserRpa({
        sessionId: pause.sessionId,
        profileId: pause.profileId || action.config?.profileId || "",
        loginRecipe: recipe,
        app: appSlug,
        tenantId,
        userId,
        goal: pendingGoal,
        actions: pendingActions,
        stopSession: true,
      });
      if (resume?.needsUserAction) {
        setPreviewUserActionPause({
          ...pause,
          liveViewUrl: resume.liveViewUrl || pause.liveViewUrl || "",
          instructions: resume.instructions || pause.instructions,
          reason: resume.reason || resume.instructions?.reason || pause.reason || "",
          sessionId: resume.sessionId || pause.sessionId,
          profileId: resume.profileId || pause.profileId,
          loginRecipe: resume.loginRecipe || pause.loginRecipe || recipe,
          pendingGoal,
          pendingActions,
        });
        const r = resume.reason || resume.instructions?.reason || "";
        setPreviewError(
          r === "task_step_failed" || r === "task_failed"
            ? "Still stuck on this step — finish it in Live View, then Continue again."
            : resume.error || "Still on login page — finish signing in, then Continue again.",
        );
        return;
      }
      if (!resume?.ok) {
        setPreviewError(resume?.error || "Resume failed.");
        return;
      }
      setPreviewUserActionPause(null);
      setPreviewRunResult({
        success: true,
        results: [
          {
            action_id: pause.actionId,
            success: true,
            data: resume,
          },
        ],
      });
      setBanner({
        severity: "success",
        message: resume?.via === "user_completed_task" || resume?.loginRecipe?.taskCoachedFromUser
          ? "Saved what you did in Live View — next run will reuse it."
          : resume?.continuedFromLiveView
            ? "Continued in the same browser session after Live View."
            : resume?.loginRecipe?.coachedFromUser
              ? "Login continued — session saved. Next run will reuse what you just did."
              : "Login continued — session saved.",
      });

      // Persist profileId + coached loginRecipe onto ALL browserRpa actions for this app
      // so sibling tools (and the next live unassisted run) reuse the same AgentCore profile.
      const coachedRecipe =
        (resume.loginRecipe && typeof resume.loginRecipe === "object"
          ? resume.loginRecipe
          : null) || recipe;
      if ((resume.profileId || coachedRecipe) && agent) {
        const appKey = String(appSlug || "").trim().toLowerCase();
        const isBrowserRpaAction = (a) => {
          if (!a || typeof a !== "object") return false;
          if (a.config?.browserRpa || a.browserRpa) return true;
          const mode = String(a.config?.automationMode || a.automationMode || "").toLowerCase();
          if (["browser_rpa", "browserrpa", "browser-rpa"].includes(mode)) return true;
          const key = String(a.action_key || a.config?.action || a.type || "")
            .toLowerCase()
            .replace(/[_\-\s]/g, "");
          return key === "browserrpa";
        };
        const actionAppKey = (a) =>
          String(a?.app || a?.app_label || a?.passportApp || a?.config?.app || "")
            .trim()
            .toLowerCase();
        const nextActions = (agent.actions || []).map((a) => {
          const samePaused = a?.action_id === pause.actionId;
          const sameApp =
            appKey && actionAppKey(a) === appKey && isBrowserRpaAction(a);
          if (!samePaused && !sameApp) return a;
          const cfg = { ...(a.config || {}) };
          if (resume.profileId) {
            cfg.profileId = resume.profileId;
            cfg.browserProfileId = resume.profileId;
          }
          if (coachedRecipe) {
            cfg.loginRecipe = { ...coachedRecipe, verified: true };
            const lu = String(coachedRecipe.loginUrl || coachedRecipe.url || "").trim();
            if (lu) cfg.loginUrl = lu;
          }
          return { ...a, config: cfg, credential: a.credential };
        });
        const patched = { ...agent, actions: nextActions };
        setAgent(patched);
        updateAgent(agentId, { actions: nextActions }).catch(() => {});
      }

      // Durable lesson from Live View coaching → agent run_memory for future runs
      if (Array.isArray(resume?.memoryUpdates) && resume.memoryUpdates.length) {
        await persistMemoryUpdates(resume.memoryUpdates);
      } else if (resume?.loggedIn && appSlug) {
        await persistMemoryUpdates([
          {
            lesson: `Login for ${appSlug} was completed in Live View. Prefer saved browser profile + verified loginRecipe; escalate to Live View quickly if still on the login page.`,
            action_id: pause.actionId || "",
          },
        ]);
      }

      if (previewTriggerEvent) {
        const updatedPriorSteps = [
          ...previewPriorSteps,
          {
            action_id: pause.actionId,
            label: (actionMap[pause.actionId] || {}).label || pause.actionId || "Browser RPA",
            output: {
              ...resume,
              // If session was stopped after the task finished, do not offer reuse.
              sessionStopped: resume.sessionStopped !== false && !resume.needsUserAction,
            },
          },
        ];
        setPreviewPriorSteps(updatedPriorSteps);
        const nextResult = await previewAgentNextStep(agent, previewTriggerEvent, {
          priorSteps: updatedPriorSteps,
          onPhase: (id, message) => setPreviewPhase({ id, message }),
        });
        if (nextResult) {
          setProposedActions(
            syncProposedActionsWithExecuteFields(agent, nextResult.proposed_actions || [], {
              priorSteps: updatedPriorSteps,
              browserSessionReuse: findReusableBrowserSession(updatedPriorSteps),
            }),
          );
          setPreviewReasoning(nextResult.reasoning || "");
          if (Array.isArray(nextResult.bedrock_calls) && nextResult.bedrock_calls.length) {
            previewBedrockCallsRef.current = previewBedrockCallsRef.current.concat(nextResult.bedrock_calls);
          }
          setPreviewApprovalEpoch((e) => e + 1);
          if (nextResult.error) setPreviewError(nextResult.error);
        }
      }
    } catch (err) {
      setPreviewError(err?.message || "Continue failed.");
    } finally {
      setPreviewContinuingUserAction(false);
      setPreviewPhase(null);
    }
  };

  const patchAgent = (patch) => setAgent((prev) => ({ ...prev, ...patch }));

  const commitTriggers = useCallback((triggers) => {
    const next = { ...(agentRef.current || {}), triggers };
    setAgent(next);
    agentRef.current = next;
    return onSaveRef.current?.(next);
  }, []);

  const commitActions = useCallback((actions) => {
    const next = syncAgentRulesWithActions({ ...(agentRef.current || {}), actions });
    setAgent(next);
    agentRef.current = next;
    return onSaveRef.current?.(next);
  }, []);

  const applyDraft = (draft) => {
    const quiet = instructionsRefineQuietRef.current;
    instructionsRefineQuietRef.current = false;
    const base = agentRef.current || agent;
    const next = applyDraftToAgent(base, draft, "all");
    if (!quiet) setBanner({ severity: "info", message: "Draft applied. Saving…" });
    else setInstructionsSaveStatus("Applying config updates…");
    enrichAgentWithCredentials(next)
      .then((patched) => {
        setAgent(patched);
        agentRef.current = patched;
        return onSave(patched, { quiet });
      })
      .then((ok) => {
        if (quiet) {
          setInstructionsSaveStatus(ok ? "Config updated" : "Config update save failed");
          if (ok) {
            window.setTimeout(() => {
              setInstructionsSaveStatus((prev) => (prev === "Config updated" ? null : prev));
            }, 2000);
          }
        } else {
          setBanner({
            severity: ok ? "success" : "error",
            message: ok ? "Draft applied and saved." : "Draft applied; save failed.",
          });
        }
      })
      .catch(() => {
        setAgent(next);
        agentRef.current = next;
        onSave(next, { quiet }).then((ok) => {
          if (quiet) {
            setInstructionsSaveStatus(ok ? "Config updated" : "Config update save failed");
          } else {
            setBanner({
              severity: ok ? "success" : "error",
              message: ok ? "Draft applied and saved." : "Draft applied; save failed.",
            });
          }
        });
      });
  };

  const applySection = (section, draft) => {
    const base = agentRef.current || agent;
    const next = applyDraftToAgent(base, draft, section);
    setBanner({ severity: "info", message: "Applying…" });
    const saveAfterApply = (target) => {
      setAgent(target);
      agentRef.current = target;
      onSave(target).then((ok) => {
        setBanner({ severity: ok ? "success" : "error", message: ok ? "Draft applied and saved." : "Draft applied; save failed." });
      });
    };
    if (section === "all" || section === "actions" || section === "watches") {
      enrichAgentWithCredentials(next)
        .then((patched) => saveAfterApply(patched))
        .catch(() => saveAfterApply(next));
    } else {
      saveAfterApply(next);
    }
  };

  if (loading) {
    return (
      <>
        <AgentsAppBar title="Agent" backTooltip="Back to agents" onBack={() => history.push("/agents")} />
        <Box display="flex" justifyContent="center" mt={10} style={{ paddingTop: AGENTS_APP_BAR_OFFSET_PX }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  const copilotDockPaddingRight = !isMobile && copilotOpen ? "min(468px, calc(40vw + 48px))" : undefined;

  return (
    <>
      <AgentsAppBar
        title={agent.name || "Agent detail"}
        onBack={handleBack}
        backTooltip="Back to agents"
      />
      <div
      style={{
        padding: isMobile
          ? `${AGENTS_APP_BAR_OFFSET_PX}px 12px 16px 12px`
          : `${AGENTS_APP_BAR_OFFSET_PX}px 24px 24px 24px`,
        minHeight: "100vh",
        width: "100%",
        flex: 1,
        boxSizing: "border-box",
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      <Prompt when={isDirty} message="You have unsaved changes." />
      <Box
        maxWidth={1100}
        mx="auto"
        style={
          copilotDockPaddingRight
            ? { paddingRight: copilotDockPaddingRight, boxSizing: "border-box" }
            : undefined
        }
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" style={{ gap: 12 }}>
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8, minWidth: 0 }}>
            <Chip size="small" color={statusColor(agent.status)} label={(agent.status || "draft").toUpperCase()} />
          </Box>
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 16 }}>
            <Button
              variant={copilotOpen ? "contained" : "outlined"}
              color="primary"
              onClick={() => setCopilotOpen((prev) => !prev)}
            >
              {copilotOpen ? "Hide AI Copilot" : "Open AI Copilot"}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AssignmentOutlinedIcon />}
              onClick={() => history.push(`/agents/${agentId}/tasks`)}
              disabled={saving || agentId === "new"}
            >
              View run history
            </Button>
            <AgentStatusControls
              status={agent.status}
              onStart={onStart}
              onPause={onPause}
              onStop={onStop}
              onPreviewAgent={onPreviewAgent}
              disabled={saving || starting || copilotBusy}
              previewLoading={previewLoading}
              starting={starting}
              startPhaseMessage={startPhaseMessage}
              startDisabled={needsPreviewBeforeStart || copilotBusy || starting}
              startDisabledReason={
                starting
                  ? startPhaseMessage || "Starting agent…"
                  : copilotBusy
                  ? "Wait for AI Copilot to finish updating the agent."
                  : startDisabledReason
              }
              buttonsOnly
            />
          </Box>
        </Box>

        {banner ? (
          <Box mb={2}>
            <Alert severity={banner.severity}>{banner.message}</Alert>
          </Box>
        ) : null}

        {!validation.canStart ? (
          <Box mb={2}>
            <Alert severity="warning">{validation.issues.join(" ")}</Alert>
          </Box>
        ) : null}

        {validation.canStart && needsPreviewBeforeStart ? (
          <Box mb={2}>
            <Alert severity="info">
              Preview the agent at least once before starting it. Start stays disabled until Preview succeeds.
            </Alert>
          </Box>
        ) : null}

        {agent.status === "error" && (agent.state?.error_streak || 0) >= (agent.policy?.kill_switch_error_streak || 5) ? (
          <Box mb={2}>
            <Alert severity="error">
              Kill switch triggered due to repeated failures. Review policy/actions before restarting.
            </Alert>
          </Box>
        ) : null}

        {isMobile ? (
          <Drawer
            anchor="right"
            variant="temporary"
            open={copilotOpen}
            onClose={() => setCopilotOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              style: {
                width: "100%",
                maxWidth: "100vw",
                boxSizing: "border-box",
                padding: 0,
                background: theme.palette.background.paper,
              },
            }}
          >
            <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" p={2} flexShrink={0} style={{ gap: 12 }}>
                <Box flex={1} minWidth={0}>
                  <Typography variant="subtitle1">AI Copilot</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Build this agent collaboratively with the assistant.
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 8, flexShrink: 0 }}>
                  {copilotConnectionBadge}
                  <IconButton aria-label="Close copilot" size="small" onClick={() => setCopilotOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box px={2} pb={2} flex={1} minHeight={0} display="flex" flexDirection="column">
                <AgentDraftingAssistant
                  ref={copilotRef}
                  copilotStorageId={agentId}
                  currentAgent={agent}
                  onApplyDraft={applyDraft}
                  onApplySection={applySection}
                  initialPrompt={copilotSeedPrompt}
                  onInitialPromptConsumed={onCopilotSeedConsumed}
                  onConnectionStatusChange={onCopilotConnectionStatus}
                  onBusyChange={onCopilotBusyChange}
                />
              </Box>
            </Box>
          </Drawer>
        ) : null}

        <Box style={{ width: "100%", minWidth: 0, position: "relative" }}>
          {copilotBusy ? (
            <Box
              aria-hidden={false}
              role="status"
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 20,
                borderRadius: 8,
                backgroundColor:
                  theme.palette.type === "dark" ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.55)",
                backdropFilter: "blur(1px)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                paddingTop: 48,
                cursor: "not-allowed",
              }}
            >
              <Paper
                elevation={2}
                style={{
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  maxWidth: 360,
                }}
              >
                <CircularProgress size={18} />
                <Typography variant="body2">
                  AI Copilot is updating this agent. Editing is paused until it finishes.
                </Typography>
              </Paper>
            </Box>
          ) : null}
          <Box
            style={{
              pointerEvents: copilotBusy ? "none" : undefined,
              opacity: copilotBusy ? 0.55 : 1,
              transition: "opacity 0.2s ease",
              userSelect: copilotBusy ? "none" : undefined,
            }}
          >
          <Box mb={2}>
            <AgentGoalSection
              agent={agent}
              onChange={onInstructionsChange}
              onCommit={flushInstructionsSave}
              onInstructionsCommit={refineConfigFromInstructions}
              saveStatus={instructionsSaveStatus}
              locked={copilotBusy}
            />
          </Box>

          <Box mb={2}>
            <AgentWatchesSection
              triggers={agent.triggers || []}
              onChange={(triggers) => patchAgent({ triggers })}
              onCommit={commitTriggers}
              user={userId}
              tenant={tenantId}
            />
          </Box>

          <Box mb={2}>
            <AgentActionsSection
              actions={agent.actions || []}
              onChange={(actions) => setAgent((prev) => syncAgentRulesWithActions({ ...prev, actions }))}
              onCommit={commitActions}
            />
          </Box>

          <Box mb={2}>
            <AgentKnowledgeSourcesSection
              knowledgeSources={agent.knowledge_sources || []}
              onChange={(knowledge_sources) => patchAgent({ knowledge_sources })}
            />
          </Box>
          </Box>
        </Box>

        <PreviewAgentModal
          open={previewOpen}
          onClose={onClosePreview}
          runId={previewRunId}
          steps={previewSteps}
          currentReasoning={previewReasoning}
          currentProposedActions={proposedActions}
          onRunApproved={onRunApproved}
          useProposalIndexAsSelection={previewFromBrain}
          previewApprovalEpoch={previewApprovalEpoch}
          agent={agent}
          onSendReply={onSendReply}
          loading={previewLoading}
          runResult={previewRunResult}
          previewError={previewError}
          previewPhase={previewPhase}
          userActionPause={previewUserActionPause}
          onContinueUserAction={onContinueUserAction}
          continuingUserAction={previewContinuingUserAction}
          formSampleWait={previewFormSample}
          onStartFormListen={onStartFormListen}
          onUseLatestFormSample={onUseLatestFormSample}
          onCancelFormSampleWait={onCancelFormSampleWait}
          onRetryFormListen={onRetryFormListen}
        />
      </Box>

      {!isMobile && copilotDockMounted ? (
        <Box
          style={{
            position: "fixed",
            top: 84,
            right: 24,
            bottom: 0,
            width: 420,
            maxWidth: "40vw",
            zIndex: 1100,
            display: copilotOpen ? "flex" : "none",
            flexDirection: "column",
            boxSizing: "border-box",
            pointerEvents: copilotOpen ? "auto" : "none",
            paddingBottom: 12,
          }}
          aria-hidden={!copilotOpen}
        >
          <Box
            component={Paper}
            style={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              height: COPILOT_FIXED_DOCK_PANEL_HEIGHT_CSS,
              maxHeight: COPILOT_FIXED_DOCK_PANEL_HEIGHT_CSS,
              borderRadius: 12,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow:
                theme.palette.type === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.45)"
                  : "0 8px 32px rgba(0,0,0,0.12)",
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" px={1.25} py={1} flexShrink={0} style={{ gap: 8 }}>
              <Box flex={1} minWidth={0}>
                <Typography variant="subtitle1" style={{ lineHeight: 1.2 }}>
                  AI Copilot
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" style={{ lineHeight: 1.35, marginTop: 2 }}>
                  {copilotBusy
                    ? "Working on your agent — editing on the left is paused."
                    : "Chat while you edit the agent on the left."}
                </Typography>
              </Box>
              <Box style={{ flexShrink: 0 }}>{copilotConnectionBadge}</Box>
            </Box>
            <Box
              px={0}
              pb={0}
              flex={1}
              minHeight={0}
              minWidth={0}
              display="flex"
              flexDirection="column"
              overflow="hidden"
              style={{ flexBasis: 0, flexGrow: 1 }}
            >
              <AgentDraftingAssistant
                ref={copilotRef}
                copilotStorageId={agentId}
                currentAgent={agent}
                onApplyDraft={applyDraft}
                onApplySection={applySection}
                initialPrompt={copilotSeedPrompt}
                onInitialPromptConsumed={onCopilotSeedConsumed}
                onConnectionStatusChange={onCopilotConnectionStatus}
                onBusyChange={onCopilotBusyChange}
                unifiedDock
              />
            </Box>
          </Box>
        </Box>
      ) : null}
    </div>
    </>
  );
};

export default AgentDetailPage;
